/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable react/no-array-index-key */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChangesContext } from '@/contexts/changes-context/changes-context';
import CheckBox from '../checkbox/checkbox';

/**
 * CheckboxGroup
 * General wrapper for a single checkbox or a group of checkboxes
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {Array of Objects}  options - Array of Objects containing title and subTitle for the checkboxes
 * @param {function} onchange - Optional, called when the state changes
 * @param {Object} subControls - Optional, object containing sub controls for the checkbox.
 * @param {function} generateControl - Optional, function used to generate subControls
 */

function CheckboxGroup({
  inputSelectorId,
  options,
  onChange,
  subControls,
  generateControl,
  changeKey,
  defaultValue,
  ariaLabel,
  compactSubControls,
  subControlsTitle,
  subControlsRequired,
}) {
  const [selected, setSelected] = useState(false);
  const { removeChange } = useContext(ChangesContext);

  // Clears any changes for the sub-controls when a checkbox is unchecked.
  useEffect(() => {
    if (!selected) {
      subControls?.forEach((control) => removeChange(control.params.updateValue));
    }
  }, [selected]);

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const selection = () => {
    if (selected) {
      setSelected(false);
      return removeChange(changeKey);
    }
    setSelected(true);
    if (onChange) return onChange(true);
    return null;
  };

  return (
    <>
      <div className="group-wrapper">
        {options?.length > 0 && options.map((entry, index) => (
          <div
            className="checkbox-group-wrapper "
            key={`${inputSelectorId}-checkbox-group-wrapper-${index}`}
          >
            <div className="checkbox-container">
              <CheckBox
                ariaLabel={`${ariaLabel} ${entry.title}`}
                key={`${inputSelectorId}-checkbox-${index}`}
                inputSelectorId={`${inputSelectorId}-checkbox-${index}`}
                type="button"
                onChange={() => selection()}
                defaultSelected={defaultValue}
              />
            </div>
            <div className="checkbox-label-container">
              <div className="checkbox-title">
                {entry.title && entry.title}
              </div>
              <div className="checkbox-subtitle">
                {entry.subTitle && entry.subTitle}
              </div>
              {index === options.length - 1 && subControls?.length > 0 && generateControl && selected && (
                <div className={`sub-controls${compactSubControls ? ' compact' : ''}`}>
                  {subControlsTitle && (
                    <div className="sub-controls-title">
                      {`${subControlsTitle}${subControlsRequired === true ? '*' : ''}`}
                    </div>
                  )}
                  {subControls.map((subControl) => (
                    <div key={subControl.id} className={subControl.params?.fullWidth ? 'full-width' : undefined}>
                      {generateControl(subControl, generateControl, selected)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <style jsx>
        {`
          .group-wrapper {
            display: flex;
            flex-direction: column;
            border-right: none;
            margin-bottom: 20px;
            width: 100%;
          }

          .checkbox-group-wrapper {
            display: flex;
            justify-content: flex-start;
            flex-basis: 5%;
          }

          .checkbox-label-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: left;
            flex-basis: 95%;
          }

          .checkbox-title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
            margin-bottom: 10px;
          }
          
          .checkbox-subtitle {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(  --font-size-subtitle);
            line-height: 1.5;
          }
          
          .sub-controls {
            display: flex;
            flex-direction: column;
            gap: 4px;
            width: 100%;
            margin: 12px 0 0 0;
            padding: 14px 16px 0 16px;
          }

          .sub-controls-title {
            font-size: var(--font-size-H3);
            font-weight: var(--font-weight-H3);
            color: var(--color-primary-dark);
            margin-bottom: 24px;
          }

          .sub-controls > div.full-width:not(:first-child) {
            border-top: 1px solid var(--color-line-grey);
            padding-top: 24px;
            margin-top: 8px;
          }

          .sub-controls > div > :global(.group-wrapper) {
            margin-bottom: 6px;
          }

          .sub-controls.compact .checkbox-title {
            font-size: var(--font-size-H3);
            font-weight: var(--font-weight-H3);
            margin-bottom: 4px;
          }

          .sub-controls.compact > div > :global(.group-wrapper) :global(.checkbox) {
            height: 28px;
            width: 28px;
          }

          .sub-controls.compact > div > :global(.group-wrapper) :global(.tick) {
            height: 16px;
            width: 16px;
            margin: 4px;
          }
          
          @media only screen and (max-width: 768px) {
            .checkbox-label-container {
              gap: 10px;
              margin-top: 5px;
            }

            .checkbox-title {
              line-height: 1.5;
              overflow-wrap: break-word;
              word-break: break-word;
            }

            .group-wrapper {
              margin-top: 10px;
            }
            .sub-controls {
              padding: 0;
            }
            .sub-controls.compact {
              padding: 14px 0 0 0;
            }

            .sub-controls.compact > div.full-width:not(:first-child) :global(.column-grid) {
              grid-template-columns: 1fr;
              row-gap: 20px;
            }
            .sub-controls.compact > div.full-width:not(:first-child) :global(.radio-button) {
              padding-left: 0;
            }
          }
        `}
      </style>
    </>
  );
}

CheckboxGroup.defaultProps = {
  inputSelectorId: null,
  onChange: () => {},
  subControls: [],
  options: [],
  generateControl: null,
  changeKey: null,
  defaultValue: null,
  ariaLabel: null,
  compactSubControls: false,
  subControlsTitle: null,
  subControlsRequired: false,
};

CheckboxGroup.propTypes = {
  inputSelectorId: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string, subTitle: PropTypes.string })),
  onChange: PropTypes.func,
  subControls: PropTypes.arrayOf(PropTypes.shape()),
  generateControl: PropTypes.func,
  changeKey: PropTypes.shape(),
  defaultValue: PropTypes.bool,
  ariaLabel: PropTypes.string,
  compactSubControls: PropTypes.bool,
  subControlsTitle: PropTypes.string,
  subControlsRequired: PropTypes.bool,
};

export default CheckboxGroup;
